import React, { useEffect, useState } from "react";

/**
 * Detects the Facebook, Instagram, Messenger, TikTok, or LinkedIn
 * in-app browser and nudges the user to open the page in Safari/Chrome.
 *
 * Rules:
 *   - Only renders when a known in-app webview UA is detected.
 *   - User can dismiss; dismissal persists via sessionStorage so the
 *     banner doesn't nag on every route change.
 *   - Pure CSS, no external deps. Safe even if ErrorBoundary catches
 *     everything else — this is one of the first things to render.
 */
const STORAGE_KEY = "ppt_iab_dismissed";

export function isInAppBrowser(ua) {
  if (!ua || typeof ua !== "string") return false;
  const u = ua.toLowerCase();
  // Patterns per Meta / TikTok / LinkedIn docs. "FBAN"/"FBAV" = Facebook
  // app; "FB_IAB"/"FB4A" = Facebook in-app browser Android; "Instagram"
  // self-identifies; Messenger uses "FBAN/MessengerLiteForiOS" variants.
  return (
    u.includes("fban") ||
    u.includes("fbav") ||
    u.includes("fb_iab") ||
    u.includes("fb4a") ||
    u.includes("fbios") ||
    u.includes("instagram") ||
    u.includes("messenger") ||
    u.includes("linkedinapp") ||
    u.includes("musical_ly") || // TikTok (iOS)
    u.includes("bytedance") // TikTok (Android)
  );
}

export default function InAppBrowserBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {}
    try {
      if (isInAppBrowser(navigator.userAgent)) setShow(true);
    } catch {}
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setShow(false);
  };

  const openExternal = () => {
    try {
      // On Android the `intent://` scheme forces Chrome. On iOS the FB
      // app honours `window.open(url, "_blank")` by handing off to
      // Safari in most recent versions. We try both.
      const url = window.location.href;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {}
  };

  if (!show) return null;

  return (
    <div
      data-testid="iab-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        background: "#0f172a",
        color: "white",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 13,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        boxShadow: "0 1px 0 rgba(255,255,255,0.08)",
      }}
    >
      <div style={{ flex: 1, lineHeight: 1.35 }}>
        <strong style={{ fontWeight: 700 }}>Heads up:</strong>{" "}
        You&apos;re viewing inside an app browser. For the best experience
        (and to sign up smoothly), tap below to open in Safari or Chrome.
      </div>
      <button
        onClick={openExternal}
        data-testid="iab-banner-open"
        style={{
          background: "#0284c7",
          color: "white",
          border: "none",
          borderRadius: 8,
          padding: "8px 12px",
          fontWeight: 600,
          cursor: "pointer",
          fontSize: 13,
          whiteSpace: "nowrap",
        }}
      >
        Open
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="iab-banner-dismiss"
        style={{
          background: "transparent",
          color: "white",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: 8,
          padding: "8px 10px",
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        ✕
      </button>
    </div>
  );
}
